import { type ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useGoBack } from '@/lib/navigation';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** 画面見出しと主要操作（docs/10-screen-spec.md 1.1） */
export const PageHeader = ({
  title,
  description,
  actions,
  back,
  badge,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** 戻る先。文字列は直前の画面が無いとき（直接開いたとき）の行き先で、通常はその画面の親にあたる一覧。関数は常にその処理を行う */
  back?: string | (() => void);
  badge?: ReactNode;
  className?: string;
}) => {
  // 直前の画面があればそこへ戻る（リスト → 企業詳細 → 戻る で、企業一覧ではなくそのリストに戻る）
  const goBack = useGoBack(typeof back === 'string' ? back : '/');

  return (
    <div className={cn('mb-4 space-y-2 lg:mb-6', className)}>
      <div className="flex items-start gap-2">
        {back && (
          <Button
            variant="ghost"
            size="icon"
            className="-ml-2 shrink-0"
            onClick={typeof back === 'function' ? back : goBack}
            aria-label="前の画面に戻る"
          >
            <ChevronLeft aria-hidden />
          </Button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="min-w-0 break-words text-h1-sm lg:text-h1">{title}</h1>
            {badge}
          </div>
          {description && (
            <div className="mt-1 text-body text-foreground-muted">{description}</div>
          )}
        </div>
        {actions && <div className="hidden shrink-0 items-center gap-2 lg:flex">{actions}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 lg:hidden">{actions}</div>}
    </div>
  );
};

/** スマホの下部固定操作（docs/09-design-system.md 5.3 BottomActions） */
export const BottomActions = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div
    className={cn(
      'safe-bottom fixed inset-x-0 bottom-bar-bottom z-20 flex gap-2 border-t border-border bg-background p-3',
      'lg:static lg:border-0 lg:bg-transparent lg:p-0',
      className,
    )}
  >
    {children}
  </div>
);

/** 一覧上部の絞り込みチップ列。ここだけ横スクロールを許容する */
export const FilterChips = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn('scroll-x -mx-4 flex gap-2 px-4 pb-1 lg:mx-0 lg:flex-wrap lg:px-0', className)}>
    {children}
  </div>
);

/** 適用中の条件チップ（個別に × で解除できる） */
export const AppliedChip = ({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) => (
  <span className="inline-flex h-8 shrink-0 items-center gap-1 rounded-control border border-border-strong bg-background px-2 text-note text-foreground">
    {label}
    <button
      type="button"
      onClick={onRemove}
      className="rounded-control p-0.5 text-foreground-muted hover:bg-muted hover:text-foreground"
      aria-label={`${label} の条件を解除する`}
    >
      ×
    </button>
  </span>
);

/** 一括操作バー（選択件数と操作内容を明示する） */
export const BulkBar = ({
  count,
  onClear,
  children,
}: {
  count: number;
  onClear: () => void;
  children: ReactNode;
}) => {
  if (count === 0) return null;
  return (
    <div className="safe-bottom fixed inset-x-0 bottom-bar-bottom z-20 flex flex-wrap items-center gap-2 border-t border-border bg-surface-raised p-3 shadow-dialog lg:static lg:rounded-card lg:border lg:shadow-none">
      <span className="tabular text-body font-semibold">
        {count.toLocaleString('ja-JP')} 件選択中
      </span>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      <Button variant="ghost" size="sm" className="ml-auto" onClick={onClear}>
        選択を解除する
      </Button>
    </div>
  );
};
